// src/InterviewPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import BACKEND_URL from "./config";
import { trackLinkedInConversion } from "./linkedin-ad-conversion-tracking/linkedin";
import { LINKEDIN_CONV_ID_INTERVIEW_COMPLETED } from "./linkedin-ad-conversion-tracking/conversionIds";
import { isMobileOrTabletDevice, trackFunnel } from "./funnel/tracking";
import { colors, ui, Card, InlineAlert } from "./participant/ui";

// MUI components for feedback UI
import Rating from "@mui/material/Rating";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

/**
 * InterviewPage (rebuilt)
 * Phases: preflight -> interview -> upload
 *
 * The old setup + tips screens are one "Before we begin" wizard (2026-08 UX
 * refresh — see AI_INTERVIEWER_UX_PLAN.md Phase 2): an auto-running system
 * check, mic+camera with self-view and live level meter, screen share, ready.
 *
 * Hard requirements:
 * - Mic required and must remain active
 * - Screen share required and must remain active
 * - If either stops AFTER interview starts => end interview => wrap+upload
 * - No transcript confirmation step
 * - AI voice selection during interview
 */

const PHASES = ["preflight", "interview", "upload"];

const OPENAI_TTS_VOICES = [
  "marin",
  "cedar",
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "fable",
  "nova",
  "onyx",
  "sage",
  "shimmer",
  "verse",
];

// Spoken + shown once, before the first question, with a single Continue
// button. Frontend-only: it never enters the transcript.
//
// An interview digs into either a product the participant uses or a job they
// get done (meta.interview_focus_type). A JTBD script never mentions a product,
// so promising to learn "how you use this product" opens the conversation on a
// premise the questions then contradict. Unknown focus keeps the product
// wording, which is what every interview said before this split.
function welcomeTextFor(focusType) {
  const closer =
    focusType === "jtbd"
      ? "how you actually get this work done"
      : "how you use this product";
  return (
    "Hello, and thanks for joining this interview. I'm an AI customer researcher. " +
    "Think of me like a documentary filmmaker and you are the star of the show! " +
    `There are no right or wrong answers - I'm just looking to learn ${closer}. ` +
    "Let's get started!"
  );
}

// Short acknowledgments played the moment the participant hits Stop & send,
// so the processing gap doesn't feel like dead air. Pre-generated in the
// selected voice during pre-flight; if that fetch failed we just stay silent.
const ACK_PHRASES = ["Got it.", "Okay, thank you.", "Thanks — one moment."];

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// Participants are mid-recording when things break, so they get plain language,
// never a status code. The backend supplies a written message for the failures
// it anticipates; `fallback` covers the rest — including responses with no JSON
// body at all, e.g. the React catch-all serving index.html for a bad route.
function participantMessage(data, fallback) {
  const msg = typeof data?.error === "string" ? data.error.trim() : "";
  return msg || fallback;
}

function getDisplaySurface(stream) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track || !track.getSettings) return null;
  const settings = track.getSettings() || {};
  return settings.displaySurface || null; // 'monitor' | 'window' | 'browser' (tab) | null
}

function humanizeEndReason(reason) {
  if (reason === "screen_stopped") return "The interview ended because screen sharing stopped.";
  if (reason === "mic_stopped") return "The interview ended because your microphone stopped.";
  if (reason === "user_ended") return "Uploading your interview…";
  return null;
}

function humanizePaymentMethod(method) {
  if (method === "paypal") return "PayPal";
  if (method === "venmo") return "Venmo";
  if (method === "amazon_gift_card") return "Amazon gift card";
  if (method === "visa_gift_card") return "Visa gift card";
  return method || null;
}

// =========================
// System checks (pre-flight wizard, step 1)
// =========================

// isMobileOrTabletDevice now lives in funnel/tracking.js so the landing page
// gate and this backstop share one definition. The check here still matters:
// someone can reach /interview/:id directly on a phone, and a device can change
// between starting and resuming.

// The old single getCompatibilityStatus() is split into per-item checks so the
// wizard can show each line passing (or failing) on its own — watching the
// checks tick green is what makes a recording product feel trustworthy.

function checkDeviceType() {
  if (isMobileOrTabletDevice()) {
    return {
      ok: false,
      detail: "Phones and tablets can’t record a screen share",
      bullets: [
        "Switch to a laptop or desktop computer.",
        "Use the latest Google Chrome or Microsoft Edge.",
      ],
    };
  }
  return { ok: true, detail: "Laptop / desktop detected" };
}

function checkRecordingApis() {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
  const hasGetUserMedia = !!md && typeof md.getUserMedia === "function";
  const hasGetDisplayMedia = !!md && typeof md.getDisplayMedia === "function";
  const hasMediaRecorder =
    typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";

  if (!hasGetUserMedia || !hasGetDisplayMedia || !hasMediaRecorder) {
    return {
      ok: false,
      detail: "This browser can’t share a screen and microphone",
      bullets: [
        "Use the latest Google Chrome or Microsoft Edge on a laptop/desktop.",
        "If you’re in Safari (or an in-app browser), switch to Chrome/Edge.",
      ],
    };
  }
  return { ok: true, detail: "Screen sharing and mic recording available" };
}

function checkRecordingFormats() {
  // Matches the MediaRecorder mimeTypes used for the session recording below.
  const videoMime = "video/webm;codecs=vp9,opus";
  const audioMime = "audio/webm;codecs=opus";

  const supported =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof window.MediaRecorder.isTypeSupported === "function" &&
    window.MediaRecorder.isTypeSupported(videoMime) &&
    window.MediaRecorder.isTypeSupported(audioMime);

  if (!supported) {
    return {
      ok: false,
      detail: "This browser can’t save the recording format we need",
      bullets: [
        "Use the latest Google Chrome or Microsoft Edge on a laptop/desktop.",
        "Safari and most mobile browsers won’t work for this recording format.",
      ],
    };
  }
  return { ok: true, detail: "Recording format supported" };
}

const SYSTEM_CHECKS = [
  { key: "device", label: "Device type", run: checkDeviceType },
  { key: "apis", label: "Browser support", run: checkRecordingApis },
  { key: "format", label: "Recording format", run: checkRecordingFormats },
];



export default function InterviewPage() {
  const { interviewId } = useParams();
  const navigate = useNavigate();

  // --- Server state ---
  const [meta, setMeta] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [interviewTypeSlug, setInterviewTypeSlug] = useState(null);
  // {phase_index, total_phases, phase_label} or null — drives the section chip.
  const [progress, setProgress] = useState(null);
  // Per-question "Show, don't tell" nudge, from show_screen_reminder in the
  // interview script. Absent in older scripts, which the server reads as false.
  const [showScreenReminder, setShowScreenReminder] = useState(false);

  // --- Phase machine ---
  const [phase, setPhase] = useState("preflight");

  // --- UI state ---
  const [error, setError] = useState("");
  const [toast, setToast] = useState(""); // small info banner text (not critical)
  // --- Pre-flight wizard ---
  const [preflightStep, setPreflightStep] = useState("system"); // system | media | screen | ready
  const [systemCheckResults, setSystemCheckResults] = useState([]); // {key,label,status,detail,bullets}
  const [systemCheckRun, setSystemCheckRun] = useState(0); // bump to re-run the checks
  const [micHeard, setMicHeard] = useState(false); // level meter registered speech

  // Funnel steps fire once per page load. Pre-flight is retryable (a denied
  // permission can be granted on a second click), and we want the step to mean
  // "this visitor got past here", not "how many attempts it took".
  const trackedRef = useRef({
    micGranted: false,
    screenGranted: false,
    firstAnswer: false,
    systemCheckFailed: false,
  });
  // --- Answer state ---
  const [answerState, setAnswerState] = useState("idle"); // idle | recording | processing
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);

  // Welcome-vs-Q&A sub-state inside the interview phase. Fresh interviews open
  // on the welcome card (Continue button only); resumed ones (refresh mid-way)
  // skip straight to the question.
  const [interviewStage, setInterviewStage] = useState("welcome"); // welcome | qa

  // The interviewer's closing line, carried onto the upload screen so the
  // participant sees the interview ended on purpose.
  const [farewellText, setFarewellText] = useState("");

  // Product-vs-job wording for the participant-facing copy. Derived rather than
  // stored so it corrects itself as soon as meta arrives.
  const isJtbdFocus = meta?.interview_focus_type === "jtbd";
  const welcomeText = welcomeTextFor(meta?.interview_focus_type);

  // --- TTS (AI voice) ---
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [voicePersona, setVoicePersona] = useState("professional"); // professional|friendly
  const [voiceGender, setVoiceGender] = useState("female"); // female|male
  const [selectedVoiceName, setSelectedVoiceName] = useState("");
  const [aiVoicePanelOpen, setAiVoicePanelOpen] = useState(false);

  // Force toolbar values to re-render on tick, e.g. mic status
  const [mediaTick, setMediaTick] = useState(0);

  // Keep refs for event handlers
  const ttsEnabledRef = useRef(true);
  useEffect(() => {
    ttsEnabledRef.current = ttsEnabled;
  }, [ttsEnabled]);

  // --- Media refs ---
  const webcamStreamRef = useRef(null); // getUserMedia stream (video+audio)
  const screenStreamRef = useRef(null); // getDisplayMedia stream (video)
  const webcamRecorderRef = useRef(null); // continuous session recorder
  const screenRecorderRef = useRef(null); // continuous session recorder
  const webcamChunksRef = useRef([]);
  const screenChunksRef = useRef([]);

  // Per-answer audio recorder
  const answerRecorderRef = useRef(null);
  const answerChunksRef = useRef([]);

  // Prevent repeated media cleanup
  const mediaCleanedUpRef = useRef(false);

  // Stored blobs for retry upload
  const webcamMasterBlobRef = useRef(null);
  const screenMasterBlobRef = useRef(null);

  // Track “session started” locally (used to enable forced-end behavior)
  const sessionStartedRef = useRef(false);

  // TTS (text to speech) fetch abort controller
  const ttsAbortRef = useRef(null);
  // --- TTS playback (WebAudio avoids autoplay issues with async) ---
  const audioCtxRef = useRef(null);
  const ttsSourceRef = useRef(null);
  // Pending delayed micro-ack, so a fast-arriving question can cancel it
  const ackTimerRef = useRef(null);
  // text -> ArrayBuffer of MP3, for audio we can generate ahead of time
  // (welcome, first question, ack phrases). Decoded at play time.
  const prefetchedTtsRef = useRef(new Map());

  const avatarUploadedRef = useRef(false);

  // Pre-flight self-view + mic level meter
  const selfViewRef = useRef(null);
  // Live "what you're sharing" preview during the interview
  const screenViewRef = useRef(null);
  const meterFillRef = useRef(null);
  const meterRafRef = useRef(null);
  const meterSourceRef = useRef(null);
  const meterAnalyserRef = useRef(null);
  const micHeardRef = useRef(false);

  // Feedback state
  const [exitRating, setExitRating] = useState(null); // 1..10
  const [exitWhy, setExitWhy] = useState("");
  const [exitSubmitting, setExitSubmitting] = useState(false);
  const [exitSubmitted, setExitSubmitted] = useState(false);
  const [exitError, setExitError] = useState("");


  // Track answer numbering + timeline for backend
  const answerIndexRef = useRef(1);
  const sessionStartMsRef = useRef(null);

  // Upload UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusLocal, setUploadStatusLocal] = useState({
    webcam: "missing", // missing|uploading|complete|failed
    screen: "missing",
  });
  // Percent per file while uploading; null means the browser couldn't compute a
  // total, which the bar renders as an indeterminate sweep.
  const [uploadProgress, setUploadProgress] = useState({ webcam: 0, screen: 0 });
  const [endReasonLocal, setEndReasonLocal] = useState(null); // user_ended|screen_stopped|mic_stopped

  // --- Derived status (for the interview side panel) ---
  // The mic *muted* state deserves a visible warning: it doesn't end the
  // interview (only a track ending does), it just silently records nothing.
  const micLooksMuted = useMemo(() => {
    const t = webcamStreamRef.current?.getAudioTracks?.()?.[0];
    return !!t && t.readyState !== "ended" && t.muted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, answerState, mediaTick]);

  // --- Upload stage derivation (for a clearer Wrap & Upload experience) ---
  const serverUpload = meta?.upload_status || {};
  const serverWebcam = serverUpload?.webcam;
  const serverScreen = serverUpload?.screen;

  const localWebcam = uploadStatusLocal.webcam;
  const localScreen = uploadStatusLocal.screen;

  // Server truth wins once a file has actually landed. Before that the local
  // state is the fresher of the two: the server reports "missing" for the whole
  // upload, and because that is a non-empty string it used to mask the local
  // "uploading" — so a participant watched "Not received" against both rows for
  // the entire upload and had no idea anything was happening.
  const mergeUploadStatus = (server, local) => {
    if (server === "complete") return "complete";
    if (local && local !== "missing") return local;
    return server || local || "missing";
  };
  const effectiveWebcam = mergeUploadStatus(serverWebcam, localWebcam);
  const effectiveScreen = mergeUploadStatus(serverScreen, localScreen);

  const bothUploaded = effectiveWebcam === "complete" && effectiveScreen === "complete";

  // If your backend sets meta.video_status to "processing" or "complete", use it to show "Finalizing…"
  const serverVideoStatus = meta?.video_status; // "processing" | "complete" | undefined

  const uploadFailed =
    effectiveWebcam === "failed" || effectiveScreen === "failed";

  // We consider "complete" only when BOTH files are uploaded AND server says complete (or doesn't track video_status)
  const uploadComplete =
    bothUploaded && (serverVideoStatus === "complete" || !serverVideoStatus);

  const finalizing =
    bothUploaded && !uploadComplete && !isUploading && !uploadFailed;

  // Small helper for a single headline/label on the Wrap screen
  const wrapStage = uploadFailed
    ? "failed"
    : uploadComplete
    ? "complete"
    : finalizing
    ? "finalizing"
    : "uploading";
  
    // ✅ LinkedIn ad conversion: "Interview Completed"
    // Fires once when upload is complete.
    useEffect(() => {
      if (wrapStage !== "complete") return;

      const key = `li_complete_tracked_${interviewId}`;
      if (sessionStorage.getItem(key) === "1") return;

      sessionStorage.setItem(key, "1");
      trackFunnel("interview_completed", { interview_id: interviewId });
      trackLinkedInConversion(LINKEDIN_CONV_ID_INTERVIEW_COMPLETED);
    }, [wrapStage, interviewId]);

    // =========================
  // 1) Load voices + interview state
  // =========================

  useEffect(() => {
    async function fetchState() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/state`);
        if (!res.ok) {
          setError(`Failed to load interview state (HTTP ${res.status}).`);
          return;
        }
        const data = await res.json();
        setMeta(data.meta || null);
        setInterviewTypeSlug(data.interview_type_slug || data?.meta?.interview_type_slug || null);
        if (data.last_question_text) setCurrentQuestion(data.last_question_text);
        if (data.progress) setProgress(data.progress);
        setShowScreenReminder(Boolean(data.show_screen_reminder));

        // A session that already started is a mid-interview refresh — greeting
        // them again with the welcome speech would be strange, so skip to Q&A.
        if (data?.meta?.session_started_at) setInterviewStage("qa");

        // Restore TTS settings if present
        const tts = data.tts_settings || data?.meta?.tts_settings;
        if (tts) {
          if (typeof tts.enabled === "boolean") setTtsEnabled(tts.enabled);
          if (typeof tts.voiceName === "string") setSelectedVoiceName(tts.voiceName);
          // Optional: style/persona mapping
          if (typeof tts.style === "string") {
            setVoicePersona(tts.style.toLowerCase() === "friendly" ? "friendly" : "professional");
          }
        }

        // Phase restore: only reliably restore upload phase (refresh-safe)
        const endedReason = data.ended_reason || data?.meta?.ended_reason;
        const videoStatus = data.video_status || data?.meta?.video_status;
        const uploadStatus = data.upload_status || data?.meta?.upload_status;

        if (endedReason || videoStatus === "processing" || videoStatus === "complete" || uploadStatus?.webcam === "complete" || uploadStatus?.screen === "complete") {
          setEndReasonLocal(endedReason || endReasonLocal);
          setPhase("upload");
          return;
        }

        // Default start phase
        setPhase("preflight");
      } catch (e) {
        console.error(e);
        setError("Failed to load interview state.");
      }
    }
    fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  // Step 1 of the wizard: run the system checks as a visible sequence. Each
  // line flips from pending to pass/fail with a small stagger, then the wizard
  // advances on its own — the user watches it work instead of trusting it.
  useEffect(() => {
    if (phase !== "preflight" || preflightStep !== "system") return;

    let cancelled = false;
    const timers = [];

    setSystemCheckResults(
      SYSTEM_CHECKS.map((c) => ({ key: c.key, label: c.label, status: "pending" }))
    );

    SYSTEM_CHECKS.forEach((check, i) => {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          const result = check.run();
          setSystemCheckResults((prev) =>
            prev.map((r) =>
              r.key === check.key
                ? {
                    ...r,
                    status: result.ok ? "pass" : "fail",
                    detail: result.detail,
                    bullets: result.bullets,
                  }
                : r
            )
          );
        }, 450 * (i + 1))
      );
    });

    timers.push(
      setTimeout(() => {
        if (cancelled) return;
        const results = SYSTEM_CHECKS.map((c) => ({ key: c.key, ok: c.run().ok }));
        const allOk = results.every((r) => r.ok);
        if (allOk) {
          setPreflightStep("media");
          return;
        }
        // Side event, not a step: these visitors are on a browser that cannot
        // record, so they were never able to continue.
        if (!trackedRef.current.systemCheckFailed) {
          trackedRef.current.systemCheckFailed = true;
          trackFunnel("preflight_system_check_failed", {
            interview_id: interviewId,
            failed_checks: results.filter((r) => !r.ok).map((r) => r.key),
          });
        }
      }, 450 * SYSTEM_CHECKS.length + 800)
    );

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, preflightStep, systemCheckRun]);

  // Keep the self-view <video> bound to the webcam stream. The element mounts
  // and unmounts as the wizard moves, so re-bind whenever media state changes.
  useEffect(() => {
    const v = selfViewRef.current;
    const stream = webcamStreamRef.current;
    if (v && stream && v.srcObject !== stream) {
      v.srcObject = stream;
      v.play().catch(() => {});
    }
    // Same re-binding dance for the shared-screen preview: participants forget
    // they're sharing when the only evidence is a status line of text.
    const sv = screenViewRef.current;
    const screenStream = screenStreamRef.current;
    if (sv && screenStream && sv.srcObject !== screenStream) {
      sv.srcObject = screenStream;
      sv.play().catch(() => {});
    }
  }, [phase, preflightStep, mediaTick]);

  // The meter runs through pre-flight AND the interview (the side panel shows
  // it as live "we can hear you" feedback); it stops when media is released.
  useEffect(() => {
    if (phase === "upload") stopMicMeter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase === "upload") {
      stopAllMediaTracks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Pre-generate the audio we know we'll need (welcome + first question + ack
  // phrases) while the participant is still reading the "You're all set" step,
  // so Start/Continue play instantly instead of waiting on TTS.
  useEffect(() => {
    if (phase !== "preflight" || preflightStep !== "ready") return;
    prefetchTtsAudio([welcomeText, currentQuestion, ...ACK_PHRASES]);
    // welcomeText is a dep because it settles once meta arrives; without it we
    // would cache the product wording and speak it in a JTBD interview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, preflightStep, currentQuestion, welcomeText]);

  // A voice/style change makes the pre-generated audio wrong; drop it and
  // re-generate the still-upcoming pieces in the new voice.
  useEffect(() => {
    prefetchedTtsRef.current.clear();
    if (phase === "interview" || (phase === "preflight" && preflightStep === "ready")) {
      const upcoming = [...ACK_PHRASES];
      if (interviewStage === "welcome") upcoming.push(welcomeText, currentQuestion);
      prefetchTtsAudio(upcoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVoiceName, voiceGender, voicePersona, ttsEnabled]);

  // =========================
  // 2) TTS (text to speech) helpers
  // =========================

  async function ensureAudioContextUnlocked() {
    if (typeof window === "undefined") return null;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;

    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }

    // Must be called from a user gesture at least once (Start Interview click is perfect)
    if (audioCtxRef.current.state === "suspended") {
      try {
        await audioCtxRef.current.resume();
      } catch (e) {
        // If this fails, we'll surface a toast when trying to speak
      }
    }

    return audioCtxRef.current;
  }

  function stopAnyTtsPlayback() {
    // A pending (not-yet-spoken) ack must not fire over whatever comes next
    if (ackTimerRef.current) {
      clearTimeout(ackTimerRef.current);
      ackTimerRef.current = null;
    }
    try {
      if (ttsSourceRef.current) ttsSourceRef.current.stop();
    } catch {}
    ttsSourceRef.current = null;
  }

  // Voice selection (single source of truth for every TTS request)
  function currentTtsVoiceStyle() {
    const style = voicePersona === "friendly" ? "Friendly" : "Professional";
    const voice =
      (selectedVoiceName && OPENAI_TTS_VOICES.includes(selectedVoiceName))
        ? selectedVoiceName
        : (voiceGender === "male" ? "cedar" : "marin");
    return { voice, style };
  }

  // Fetch MP3 for a text we know we'll speak soon (welcome, first question,
  // ack phrases) and cache the raw bytes. Failures are silent — playback just
  // falls back to fetching on demand.
  async function prefetchTtsAudio(texts) {
    if (!ttsEnabledRef.current) return;
    const { voice, style } = currentTtsVoiceStyle();
    await Promise.all(
      (texts || []).filter((t) => t && t.trim() && !prefetchedTtsRef.current.has(t)).map(async (text) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text, enabled: true, voice, style }),
          });
          if (!res.ok || res.status === 204) return;
          const buf = await res.arrayBuffer();
          if (buf && buf.byteLength > 0) prefetchedTtsRef.current.set(text, buf);
        } catch (e) {
          console.warn("TTS prefetch failed:", e);
        }
      })
    );
  }

  // Decode + play an MP3 ArrayBuffer through the shared AudioContext.
  async function playMp3Buffer(ctx, buf) {
    let audioBuffer;
    try {
      // Some browsers require a copy
      audioBuffer = await ctx.decodeAudioData(buf.slice(0));
    } catch (e) {
      console.warn("decodeAudioData failed:", e);
      setToast("AI Voice audio couldn’t be decoded in this browser.");
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(ctx.destination);
    src.start(0);
    ttsSourceRef.current = src;
  }

  // Spoken filler while the answer is processing. Cached-audio only: if the
  // pre-flight prefetch didn't succeed, we stay silent rather than spend a
  // round-trip on filler. Played after a short human-like beat — an instant
  // "Got it" the millisecond they click reads as canned, not as listening.
  function playRandomAck() {
    if (!ttsEnabledRef.current) return;
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state !== "running") return;
    const cached = ACK_PHRASES
      .map((t) => prefetchedTtsRef.current.get(t))
      .filter(Boolean);
    if (!cached.length) return;
    const buf = cached[Math.floor(Math.random() * cached.length)];
    if (ackTimerRef.current) clearTimeout(ackTimerRef.current);
    ackTimerRef.current = setTimeout(() => {
      ackTimerRef.current = null;
      stopAnyTtsPlayback();
      playMp3Buffer(ctx, buf).catch(() => {});
    }, 700 + Math.random() * 500);
  }

  async function speak(text) {
    if (!ttsEnabledRef.current) return;
    if (!text || !text.trim()) return;

    // Stop any previous TTS
    stopAnyTtsPlayback();

    // Ensure audio context is available (if not unlocked, we'll tell the user)
    const ctx = await ensureAudioContextUnlocked();
    if (!ctx || ctx.state !== "running") {
      setToast("AI Voice is blocked by your browser. Click “Start interview” (or any button) once to enable audio.");
      return;
    }

    // Abort previous in-flight TTS fetch
    if (ttsAbortRef.current) {
      try { ttsAbortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    ttsAbortRef.current = controller;

    try {
      // Pre-generated audio (welcome, first question, acks) plays instantly.
      const cached = prefetchedTtsRef.current.get(text);
      if (cached) {
        await playMp3Buffer(ctx, cached);
        return;
      }

      // Otherwise fetch the MP3, decode, play. One retry: a transient failure
      // here costs the participant the spoken question entirely, and two
      // questions went unspoken in a production interview (2026-08-06).
      const { voice, style } = currentTtsVoiceStyle();
      let res = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          res = await fetch(`${BACKEND_URL}/api/tts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
            body: JSON.stringify({ text, enabled: true, voice, style }),
          });
          if (res.ok || res.status === 204) break;
          console.warn(`TTS HTTP ${res.status} (attempt ${attempt + 1})`);
        } catch (e) {
          if (e?.name === "AbortError") return;
          console.warn(`TTS fetch failed (attempt ${attempt + 1}):`, e);
          res = null;
        }
        if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
      }

      if (res && res.status === 204) return;

      if (!res || !res.ok) {
        // Losing the spoken question is cosmetic — the text is still on screen —
        // so say that rather than pointing a participant at server logs.
        setToast("The spoken question isn’t available right now. You can read it above and answer as normal.");
        return;
      }

      const buf = await res.arrayBuffer();
      await playMp3Buffer(ctx, buf);

    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("TTS playback failed:", e);
      setToast("The spoken question isn’t available right now. You can read it above and answer as normal.");
    }
  }

  async function persistTtsSettings() {
    // Persist to server when ending; also used for state fetch convenience
    const payload = {
      enabled: ttsEnabled,
      voiceName: selectedVoiceName || null,
      style: voicePersona === "friendly" ? "Friendly" : "Professional",
    };
    return payload;
  }

  // =========================
  // 3) Backend helpers: mark started / end interview
  // =========================
  async function markInterviewStarted() {
    try {
      await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/mark-started`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } catch (e) {
      console.warn("mark-started failed:", e);
    }
  }

  async function endInterviewOnServer(reason) {
    try {
      const tts_settings = await persistTtsSettings();
      await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, tts_settings }),
      });
    } catch (e) {
      console.warn("end endpoint failed:", e);
    }
  }

  async function submitExitFeedback() {
    setExitError("");
    const rating = exitRating;
    const why = (exitWhy || "").trim();

    if (!rating || rating < 1 || rating > 10) {
      setExitError("Please choose a rating from 1 to 10.");
      return;
    }
    // "why" is optional — a required essay right after a long interview
    // suppressed feedback; the backend accepts it empty.

    setExitSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, why }),
      });

      if (!res.ok) {
        const data = await safeJson(res);
        setExitError(data?.error || `Failed to submit feedback (HTTP ${res.status}).`);
        return;
      }

      setExitSubmitted(true);
    } catch (e) {
      setExitError("Failed to submit feedback. Please try again.");
    } finally {
      setExitSubmitting(false);
    }
  }

  // =========================
  // 4) Setup: enable mic+camera, screen share
  // =========================
  async function enableMicAndCamera() {
    setError("");
    setToast("Requesting microphone and camera access…");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          width: { ideal: 640 },
          height: { ideal: 360 },
          frameRate: { ideal: 30, max: 30 },
        },
      });

      webcamStreamRef.current = stream;

      // Try to enforce ~360p on the camera track (best-effort)
      try {
        const vt = stream.getVideoTracks?.()?.[0];
        if (vt?.applyConstraints) {
          await vt.applyConstraints({
            width: { ideal: 640 },
            height: { ideal: 360 },
            frameRate: { ideal: 30, max: 30 },
          });
        }
      } catch {}

      setMediaTick((t) => t + 1);
      setToast("");

      if (!trackedRef.current.micGranted) {
        trackedRef.current.micGranted = true;
        trackFunnel("preflight_mic_granted", { interview_id: interviewId });
      }

      // The self-view + meter take over from here as the visible confirmation.
      startMicMeter();

      // Forced-end watcher (mic)
      const audioTracks = stream.getAudioTracks();
      if (audioTracks && audioTracks[0]) {
        const micTrack = audioTracks[0];

        micTrack.onended = () => {
          // If session already started, treat as forced end (wrap+upload)
          if (sessionStartedRef.current) {
            stopAndUpload("mic_stopped");
          }
          setMediaTick((t) => t + 1);
        };

        // Mute doesn't end the interview, but the side panel warns about it —
        // without these the muted state would never trigger a re-render.
        micTrack.onmute = () => setMediaTick((t) => t + 1);
        micTrack.onunmute = () => setMediaTick((t) => t + 1);
      }

      return true;
    } catch (e) {
      console.error(e);
      setToast("");
      setError("Could not access microphone and camera. Please allow access and try again.");
      return false;
    }
  }

  async function enableScreenShare() {
    setError("");
    setToast("Requesting screen sharing…");

    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 15, max: 15 },
        },
      });

      // Where possible: block tab sharing. Allow only Entire Screen ('monitor') or Window ('window').
      const surface = getDisplaySurface(screen);
      if (surface === "browser") {
        // Stop tracks immediately and require re-share
        screen.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;

        setToast("");
        setError("Please share your Entire Screen or a Window (not a browser tab). Click “Share my screen” to try again.");

        // Force UI re-render of toolbar pills if you're using mediaTick
        try { setMediaTick(t => t + 1); } catch {}

        return false;
      }

      // Try to enforce 15fps on the screen track (best-effort; not all browsers honor it)
      try {
        const st = screen.getVideoTracks?.()?.[0];
        if (st?.applyConstraints) {
          await st.applyConstraints({ frameRate: { ideal: 15, max: 15 } });
        }
      } catch {}

      screenStreamRef.current = screen;
      setMediaTick((t) => t + 1);
      setToast("");

      // Fires only for a surface we accept — a rejected tab-share above returns
      // before here, so this step means "actually sharing", not "clicked share".
      if (!trackedRef.current.screenGranted) {
        trackedRef.current.screenGranted = true;
        trackFunnel("preflight_screen_granted", {
          interview_id: interviewId,
          display_surface: surface,
        });
      }

      // Forced-end watcher (screen share)
      const videoTracks = screen.getVideoTracks();
      if (videoTracks && videoTracks[0]) {
        const screenTrack = videoTracks[0];
        screenTrack.onended = () => {
          if (sessionStartedRef.current) {
            stopAndUpload("screen_stopped");
          }
          setMediaTick((t) => t + 1);
        };
      }

      return true;
    } catch (e) {
      console.error(e);
      setToast("");
      setError("Screen sharing is required. Please share your screen and try again.");
      return false;
    }
  }

  // =========================
  // 4B) Mic level meter (pre-flight "we can hear you" check)
  // =========================
  function startMicMeter() {
    const stream = webcamStreamRef.current;
    if (!stream) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    stopMicMeter();

    try {
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      // Analyser only — never connect to destination, or the user hears themselves.
      source.connect(analyser);
      meterSourceRef.current = source;
      meterAnalyserRef.current = analyser;

      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const d = (data[i] - 128) / 128;
          sum += d * d;
        }
        const rms = Math.sqrt(sum / data.length);
        const level = Math.min(1, rms * 4);

        // Written directly to the DOM: a 60fps meter must not re-render React.
        if (meterFillRef.current) {
          meterFillRef.current.style.width = `${Math.round(level * 100)}%`;
        }
        if (level > 0.1 && !micHeardRef.current) {
          micHeardRef.current = true;
          setMicHeard(true);
        }
        meterRafRef.current = requestAnimationFrame(tick);
      };
      meterRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn("Mic meter failed:", e);
      // If the meter can't run, don't block the interview on it.
      micHeardRef.current = true;
      setMicHeard(true);
    }
  }

  function stopMicMeter() {
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    meterRafRef.current = null;
    try {
      meterSourceRef.current?.disconnect();
    } catch {}
    meterSourceRef.current = null;
    meterAnalyserRef.current = null;
  }

  function hasActiveMic() {
    const s = webcamStreamRef.current;
    if (!s) return false;
    const t = s.getAudioTracks()?.[0];
    return !!t && t.readyState !== "ended";
  }

  function hasActiveScreen() {
    const s = screenStreamRef.current;
    if (!s) return false;
    const t = s.getVideoTracks()?.[0];
    return !!t && t.readyState !== "ended";
  }

  // =========================
  // 5) Session recording start/stop + upload
  // =========================
  async function startSessionRecording() {
    setError("");
    setToast("");

    const webcamStream = webcamStreamRef.current;
    const screenStream = screenStreamRef.current;

    if (!webcamStream || !hasActiveMic()) {
      setError("Microphone is required to start the interview.");
      return false;
    }
    if (!screenStream || !hasActiveScreen()) {
      setError("Screen sharing is required to start the interview.");
      return false;
    }

    try {
      // Start continuous recorders (webcam + screen)
      webcamChunksRef.current = [];
      screenChunksRef.current = [];

      // Bitrate tuning to reduce file sizes
      // Adjust these numbers if you find quality too low/high.
      const WEBCAM_VIDEO_BPS = 600_000;  // ~0.6 Mbps (good for 360p talking head)
      const WEBCAM_AUDIO_BPS = 64_000;   // 64 kbps opus
      const SCREEN_VIDEO_BPS  = 1_500_000; // ~1.5 Mbps for UI screen capture at 15fps

      const webcamRecorder = new MediaRecorder(webcamStream, {
        mimeType: "video/webm;codecs=vp9,opus",
        videoBitsPerSecond: WEBCAM_VIDEO_BPS,
        audioBitsPerSecond: WEBCAM_AUDIO_BPS,
      });

      const screenRecorder = new MediaRecorder(screenStream, {
        mimeType: "video/webm;codecs=vp9,opus",
        videoBitsPerSecond: SCREEN_VIDEO_BPS,
      });

      webcamRecorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) webcamChunksRef.current.push(ev.data);
      };
      screenRecorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) screenChunksRef.current.push(ev.data);
      };

      webcamRecorderRef.current = webcamRecorder;
      screenRecorderRef.current = screenRecorder;

      webcamRecorder.start(1000);
      screenRecorder.start(1000);

      sessionStartedRef.current = true;

      // initialize per-session counters
      answerIndexRef.current = 1;
      sessionStartMsRef.current = Date.now();

      // Persist started so refresh can restore upload state later
      await markInterviewStarted();

      return true;
    } catch (e) {
      console.error(e);
      setError("Failed to start recording. Please refresh and try again.");
      return false;
    }
  }

  function stopAllMediaTracks() {
    // Avoid re-running
    if (mediaCleanedUpRef.current) return;
    mediaCleanedUpRef.current = true;

    // Stop webcam mic+camera tracks
    try {
      const webcamStream = webcamStreamRef.current;
      if (webcamStream) {
        webcamStream.getTracks().forEach((t) => {
          try { t.onended = null; } catch {}
          try { t.onmute = null; } catch {}
          try { t.onunmute = null; } catch {}
          try { t.stop(); } catch {}
        });
      }
    } catch {}

    // Stop screen-share tracks
    try {
      const screenStream = screenStreamRef.current;
      if (screenStream) {
        screenStream.getTracks().forEach((t) => {
          try { t.onended = null; } catch {}
          try { t.onmute = null; } catch {}
          try { t.onunmute = null; } catch {}
          try { t.stop(); } catch {}
        });
      }
    } catch {}

    // Clear refs so status pills show Off
    webcamStreamRef.current = null;
    screenStreamRef.current = null;

    // Force status bar to re-render
    setMediaTick((t) => t + 1);
  }

  async function stopAndUpload(reason) {
    // Prevent double calls
    if (phase === "upload") return;

    setEndReasonLocal(reason);
    setPhase("upload");
    setIsUploading(true);
    setUploadStatusLocal({ webcam: "uploading", screen: "uploading" });
    setError("");

    // Persist end reason immediately (important for refresh-safe upload)
    await endInterviewOnServer(reason);

    // Stop per-answer recorder if active
    try {
      if (answerRecorderRef.current && answerRecorderRef.current.state !== "inactive") {
        answerRecorderRef.current.stop();
      }
    } catch {}

    // Stop continuous recorders
    try {
      if (webcamRecorderRef.current && webcamRecorderRef.current.state !== "inactive") webcamRecorderRef.current.stop();
    } catch {}
    try {
      if (screenRecorderRef.current && screenRecorderRef.current.state !== "inactive") screenRecorderRef.current.stop();
    } catch {}

    // Release mic/camera/screen-share now that we're in Upload stage
    stopAllMediaTracks();

    // Give MediaRecorder a moment to flush data
    await new Promise((r) => setTimeout(r, 1200));

    try {
      const webcamBlob = new Blob(webcamChunksRef.current || [], { type: "video/webm" });
      const screenBlob = new Blob(screenChunksRef.current || [], { type: "video/webm" });

      webcamMasterBlobRef.current = webcamBlob;
      screenMasterBlobRef.current = screenBlob;

      await uploadVideos(webcamBlob, screenBlob);
      await refreshStateAfterUpload();
      setIsUploading(false);
    } catch (e) {
      console.error(e);
      setIsUploading(false);
      setUploadStatusLocal({ webcam: "failed", screen: "failed" });
      setError("Upload failed. Keep this tab open and retry.");
    }
  }

  async function uploadOneVideo(type, blob, filename) {
    try {
      setUploadStatusLocal((s) => ({ ...s, [type]: "uploading" }));
      setUploadProgress((p) => ({ ...p, [type]: 0 }));

      const fd = new FormData();
      fd.append("file", blob, filename);

      // XHR rather than fetch: these are multi-minute uploads of a whole
      // session's video, and fetch cannot report upload progress. Without a
      // percentage the participant is asked to sit and wait at a screen with no
      // sign of movement.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          `${BACKEND_URL}/api/interviews/${interviewId}/upload-video?type=${type}`
        );

        xhr.upload.onprogress = (ev) => {
          // lengthComputable is false on some proxies; the bar falls back to
          // an indeterminate animation in that case.
          const pct = ev.lengthComputable && ev.total > 0
            ? Math.min(100, Math.round((ev.loaded / ev.total) * 100))
            : null;
          setUploadProgress((p) => ({ ...p, [type]: pct }));
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress((p) => ({ ...p, [type]: 100 }));
            resolve();
          } else {
            reject(new Error(`${type} upload failed: HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error(`${type} upload failed: network error`));
        xhr.onabort = () => reject(new Error(`${type} upload aborted`));

        xhr.send(fd);
      });

      setUploadStatusLocal((s) => ({ ...s, [type]: "complete" }));
      return true;
    } catch (e) {
      console.error(e);
      setUploadStatusLocal((s) => ({ ...s, [type]: "failed" }));
      throw e;
    }
  }

  async function uploadVideos(webcamBlob, screenBlob) {
    // 1) Try parallel uploads first
    const results = await Promise.allSettled([
      uploadOneVideo("webcam", webcamBlob, "webcam_master.webm"),
      uploadOneVideo("screen", screenBlob, "screen_master.webm"),
    ]);

    const webcamOk = results[0].status === "fulfilled";
    const screenOk = results[1].status === "fulfilled";

    // If both succeeded, we’re done
    if (webcamOk && screenOk) return;

    // 2) Fallback: retry failed ones sequentially
    // (Some networks/servers behave better with one upload at a time.)
    console.warn("Parallel upload failed; falling back to sequential retry.");

    // Optional tiny pause before retry to avoid immediate contention
    await new Promise((r) => setTimeout(r, 250));

    if (!webcamOk) {
      await uploadOneVideo("webcam", webcamBlob, "webcam_master.webm");
    }

    if (!screenOk) {
      await uploadOneVideo("screen", screenBlob, "screen_master.webm");
    }
  }

  async function refreshStateAfterUpload() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/state`);
      if (res.ok) {
        const data = await res.json();
        setMeta(data.meta || null);
        setInterviewTypeSlug(data.interview_type_slug || data?.meta?.interview_type_slug || null);
      }
    } catch {}
  }

  async function retryUpload() {
    setError("");
    setIsUploading(true);
    setUploadStatusLocal({ webcam: "uploading", screen: "uploading" });

    try {
      const webcamBlob = webcamMasterBlobRef.current;
      const screenBlob = screenMasterBlobRef.current;

      if (!webcamBlob || !screenBlob) {
        throw new Error("Missing blobs for retry");
      }

      await uploadVideos(webcamBlob, screenBlob);
      await refreshStateAfterUpload();
      setIsUploading(false);
    } catch (e) {
      console.error(e);
      setIsUploading(false);
      setUploadStatusLocal({ webcam: "failed", screen: "failed" });
      setError("Upload failed again. Please restart the interview.");
    }
  }

  // =========================
  // 6) Interview answering loop (per-answer audio)
  // =========================
  async function startAnswerRecording() {
    setError("");
    if (!hasActiveMic() || !hasActiveScreen()) {
      // This should not happen if watchers work, but keep it safe.
      stopAndUpload(!hasActiveScreen() ? "screen_stopped" : "mic_stopped");
      return;
    }

    // Kick off avatar capture on the first answer (don’t block recording)
    captureAndUploadAvatarOnce();

    try {
      const stream = webcamStreamRef.current;
      const audioTrack = stream.getAudioTracks()[0];
      const audioOnlyStream = new MediaStream([audioTrack]);

      answerChunksRef.current = [];
      const recorder = new MediaRecorder(audioOnlyStream, { mimeType: "audio/webm;codecs=opus" });

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) answerChunksRef.current.push(ev.data);
      };

      answerRecorderRef.current = recorder;
      recorder.start(200);

      setAnswerState("recording");
    } catch (e) {
      console.error(e);
      setError("Could not start recording your answer. Please refresh and try again.");
    }
  }

  async function stopAnswerAndSend() {
    if (!answerRecorderRef.current) return;

    setAnswerState("processing");

    // Spoken "got it" the instant they stop, so the processing gap isn't dead air.
    playRandomAck();

    // Wait for the recorder's own stop event (it flushes the final chunk)
    // instead of a fixed sleep; the timeout is only a guard against a
    // recorder that never fires it.
    try {
      const recorder = answerRecorderRef.current;
      if (recorder.state !== "inactive") {
        await new Promise((resolve) => {
          const guard = setTimeout(resolve, 1500);
          recorder.addEventListener(
            "stop",
            () => {
              clearTimeout(guard);
              resolve();
            },
            { once: true }
          );
          try {
            recorder.stop();
          } catch {
            clearTimeout(guard);
            resolve();
          }
        });
      }
    } catch {}

    try {
      const blob = new Blob(answerChunksRef.current || [], { type: "audio/webm" });

      const fd = new FormData();
      const answerIndex = answerIndexRef.current;

      // approximate global offset for timestamps
      const offsetSeconds = sessionStartMsRef.current
        ? (Date.now() - sessionStartMsRef.current) / 1000
        : 0;

      fd.append("file", blob, `answer_${answerIndex}.webm`);
      fd.append("answer_index", String(answerIndex));
      fd.append("offset_seconds", String(offsetSeconds));

      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/answers`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        // The backend writes participant-facing guidance for the failures that
        // actually happen here — a transcription outage returns 502 with "check
        // your microphone connection and try again". Showing the status code
        // instead threw that away and read as a crash to someone being recorded.
        const data = await safeJson(res);
        console.warn("Answer submit failed:", res.status, data);
        setAnswerState("idle");
        setError(
          participantMessage(
            data,
            "We couldn’t save that answer. Your recording is still running — please try answering again."
          )
        );
        return;
      }

      // The server accepted an answer, so the interview is genuinely underway.
      // Fires before the completion branch below so a one-answer interview
      // still records that it started.
      if (!trackedRef.current.firstAnswer) {
        trackedRef.current.firstAnswer = true;
        trackFunnel("interview_first_answer_submitted", { interview_id: interviewId });
      }

      const data = await safeJson(res);

      // ✅ END THE INTERVIEW if backend says we're done
      if (data?.control?.interview_complete) {
        // Show + speak the goodbye before wrapping — previously the closing
        // was written to the transcript but the participant never heard it.
        const finalMessage = data?.next_question_text || "";
        if (finalMessage) {
          setCurrentQuestion(finalMessage);
          setFarewellText(finalMessage);
          speak(finalMessage);
        }
        setAnswerState("idle");
        await stopAndUpload("user_ended"); // keep reason valid for your /end endpoint
        return;
      }

      // otherwise continue normally
      const nextQ = data?.next_question_text || "";
      setCurrentQuestion(nextQ);
      if (data?.progress) setProgress(data.progress);
      setShowScreenReminder(Boolean(data?.show_screen_reminder));
      speak(nextQ);

      // increment only after successful processing
      answerIndexRef.current += 1;

      setAnswerState("idle");
    } catch (e) {
      console.error(e);
      setAnswerState("idle");
      setError("Failed to submit answer. Please check your connection and try again.");
    }
  }

  // =========================
  // 6B) Grab avatar image of interviewee
  // =========================
  async function captureAndUploadAvatarOnce() {
    // Only do this once per page load/session
    if (avatarUploadedRef.current) return;
    avatarUploadedRef.current = true;

    try {
      const stream = webcamStreamRef.current;
      if (!stream) return;

      const videoTrack = stream.getVideoTracks?.()?.[0];
      if (!videoTrack) return;

      let blob = null;

      // Option A: ImageCapture (works in many Chromium browsers)
      if (window.ImageCapture) {
        try {
          const ic = new ImageCapture(videoTrack);
          const bitmap = await ic.grabFrame();

          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(bitmap, 0, 0);

          blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.85)
          );
        } catch {
          // fall through to canvas+video method
        }
      }

      // Option B: draw from a hidden <video> element
      if (!blob) {
        const videoEl = document.createElement("video");
        videoEl.playsInline = true;
        videoEl.muted = true;
        videoEl.srcObject = stream;

        // Wait for enough data to draw a frame
        await videoEl.play().catch(() => {});
        await new Promise((r) => {
          if (videoEl.readyState >= 2) return r();
          videoEl.onloadeddata = () => r();
        });

        const w = videoEl.videoWidth || 640;
        const h = videoEl.videoHeight || 360;

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoEl, 0, 0, w, h);

        blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.85)
        );

        // Clean up
        try { videoEl.pause(); } catch {}
        videoEl.srcObject = null;
      }

      if (!blob) return;

      // Upload to backend
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");

      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/avatar`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        // Don’t break the interview if avatar fails
        console.warn("Avatar upload failed:", res.status, await res.text().catch(() => ""));
      }
    } catch (e) {
      console.warn("captureAndUploadAvatarOnce error:", e);
    }
  }

  // =========================
  // 7) Phase transitions
  // =========================
  async function handleStartInterview() {
    await ensureAudioContextUnlocked();
    setError("");
    const ok = await startSessionRecording();
    if (!ok) return;

    setPhase("interview");

    // Fresh interviews open on the welcome card; resumed ones go straight to
    // the current question.
    if (interviewStage === "welcome") {
      speak(welcomeText);
    } else {
      speak(currentQuestion);
    }
  }

  function handleWelcomeContinue() {
    setInterviewStage("qa");
    // speak() stops the welcome audio if it's still going
    speak(currentQuestion);
  }

  async function handleManualEndInterview() {
    setConfirmEndOpen(false);
    await stopAndUpload("user_ended");
  }

  function restartInterview() {
    if (interviewTypeSlug) {
      navigate(`/participate/${interviewTypeSlug}`);
    } else {
      // Worst-case fallback: go home
      navigate(`/`);
    }
  }

  // =========================
  // Render
  // =========================
  return (
    <ZoomLitePage>
      <style>{processingCss + preflightCss}</style>

      {/* JTBD interview types store the job in product_name, so labelling it
          "Product:" put the wrong noun on a job the participant is about to be
          asked about. */}
      <HeaderRow
        title="AI Interview"
        subtitle={
          meta?.product_name
            ? `${isJtbdFocus ? "Topic" : "Product"}: ${meta.product_name}`
            : ""
        }
      />

      <Stepper current={phase} />

      {toast ? <InlineAlert type="info">{toast}</InlineAlert> : null}
      {error ? <InlineAlert type="error">{error}</InlineAlert> : null}

      {phase === "preflight" && (
        <Card>
          <div className="pf-grid">
            {/* Left rail: where you are in the pre-flight */}
            <div style={styles.pfRail}>
              <PfRailItem
                n={1}
                label="System check"
                state={preflightStep === "system" ? "active" : "done"}
              />
              <PfRailItem
                n={2}
                label="Mic & camera"
                state={
                  preflightStep === "media"
                    ? "active"
                    : hasActiveMic()
                    ? "done"
                    : "todo"
                }
              />
              <PfRailItem
                n={3}
                label="Screen share"
                state={
                  preflightStep === "screen"
                    ? "active"
                    : hasActiveScreen()
                    ? "done"
                    : "todo"
                }
              />
              <PfRailItem
                n={4}
                label="Ready"
                state={preflightStep === "ready" ? "active" : "todo"}
              />
            </div>

            {/* Right: the active step */}
            <div>
              {preflightStep === "system" && (
                <>
                  <h2 style={styles.h2}>Quick system check</h2>
                  <p style={styles.muted}>
                    Making sure this computer can record the interview — takes a
                    few seconds, nothing to do.
                  </p>

                  <div style={{ display: "grid", gap: 10 }}>
                    {systemCheckResults.map((r) => (
                      <CheckRow key={r.key} result={r} />
                    ))}
                  </div>

                  {systemCheckResults.some((r) => r.status === "fail") ? (
                    <>
                      <div style={{ marginTop: 14 }}>
                        <div style={styles.sectionTitle}>How to fix it</div>
                        <ul style={styles.ul}>
                          {systemCheckResults
                            .filter((r) => r.status === "fail")
                            .flatMap((r) => r.bullets || [])
                            .map((b, idx) => (
                              <li key={idx}>{b}</li>
                            ))}
                        </ul>
                      </div>
                      <div style={styles.rowRight}>
                        <button
                          style={styles.secondaryBtn}
                          onClick={() => setSystemCheckRun((n) => n + 1)}
                        >
                          Run check again
                        </button>
                        <button
                          style={styles.secondaryBtn}
                          onClick={() => window.location.reload()}
                        >
                          Reload page
                        </button>
                      </div>
                    </>
                  ) : null}
                </>
              )}

              {preflightStep === "media" && (
                <>
                  <h2 style={styles.h2}>Microphone &amp; camera</h2>
                  <p style={styles.muted}>
                    Your mic carries the whole interview, so let&rsquo;s make sure it
                    works. You&rsquo;ll see exactly what the camera captures — nothing
                    is recorded until you start the interview.
                  </p>

                  {!hasActiveMic() ? (
                    <>
                      <button style={styles.accentBtn} onClick={enableMicAndCamera}>
                        Enable mic &amp; camera
                      </button>
                      <p style={styles.mutedSmall}>
                        Your browser will ask for permission — choose Allow.
                      </p>
                    </>
                  ) : (
                    <>
                      <video
                        ref={selfViewRef}
                        className="pf-selfview"
                        muted
                        playsInline
                        autoPlay
                      />
                      <div style={{ marginTop: 12, maxWidth: 340 }}>
                        <div className="pf-meter">
                          <div className="pf-meter-fill" ref={meterFillRef} />
                        </div>
                        <div style={micHeard ? styles.pfHeardOk : styles.pfHeardHint}>
                          {micHeard
                            ? "✓ We can hear you"
                            : "Say something — the bar moves when we hear you"}
                        </div>
                        {!micHeard ? (
                          <div style={styles.mutedSmall}>
                            Bar not moving? Check your system&rsquo;s microphone input
                            device, then try again.
                          </div>
                        ) : null}
                      </div>
                      <div style={styles.rowRight}>
                        <button
                          style={{
                            ...styles.accentBtn,
                            ...(micHeard ? {} : styles.btnDisabled),
                          }}
                          disabled={!micHeard}
                          onClick={() => setPreflightStep("screen")}
                        >
                          Continue
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {preflightStep === "screen" && (
                <>
                  <h2 style={styles.h2}>Screen share</h2>
                  <p style={styles.muted}>
                    You&rsquo;ll show your real work as you talk, so we ask you to
                    share your screen. When the picker opens, choose:
                  </p>

                  <div style={styles.pfPickerRow}>
                    <div style={styles.pfPickerGood}>
                      <div style={styles.pfPickerTitle}>🖥 Entire Screen</div>
                      <div style={styles.pfPickerNote}>Recommended</div>
                    </div>
                    <div style={styles.pfPickerGood}>
                      <div style={styles.pfPickerTitle}>🪟 Window</div>
                      <div style={styles.pfPickerNote}>Also fine</div>
                    </div>
                    <div style={styles.pfPickerBad}>
                      <div style={styles.pfPickerTitle}>⛔ Browser tab</div>
                      <div style={styles.pfPickerNote}>Won&rsquo;t work</div>
                    </div>
                  </div>

                  {!hasActiveScreen() ? (
                    <div style={{ marginTop: 14 }}>
                      <button style={styles.accentBtn} onClick={enableScreenShare}>
                        Share my screen
                      </button>
                      <p style={styles.mutedSmall}>
                        Close tabs or windows you don&rsquo;t want visible before
                        sharing.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginTop: 14 }}>
                        <InlineAlert type="success">
                          Screen sharing is active — you&rsquo;re sharing your screen.
                        </InlineAlert>
                      </div>
                      <div style={styles.rowRight}>
                        <button
                          style={styles.accentBtn}
                          onClick={() => setPreflightStep("ready")}
                        >
                          Continue
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {preflightStep === "ready" && (
                <>
                  <h2 style={styles.h2}>You&rsquo;re all set</h2>
                  <p style={styles.muted}>
                    Three things that make a great interview:
                  </p>

                  <div style={{ display: "grid", gap: 10 }}>
                    <PfTip n="1">
                      <strong>Think out loud.</strong> Narrate what you&rsquo;re doing
                      as you go — the AI interviewer follows your words.
                    </PfTip>
                    <PfTip n="2">
                      <strong>Show your real work.</strong> A recent, real example
                      beats a polished demo every time.
                    </PfTip>
                    <PfTip n="3">
                      <strong>Keep this tab open</strong> until the upload finishes
                      at the end.
                    </PfTip>
                  </div>

                  <Divider />

                  {hasActiveMic() && hasActiveScreen() ? (
                    <div style={styles.rowBetween}>
                      <div style={styles.mutedSmall}>
                        Recording starts when you click — the interviewer will
                        greet you and explain how it works.
                      </div>
                      <button style={styles.accentBtnBig} onClick={handleStartInterview}>
                        Start interview
                      </button>
                    </div>
                  ) : (
                    <>
                      <InlineAlert type="warning">
                        {!hasActiveMic()
                          ? "Your microphone turned off along the way."
                          : "Your screen share stopped along the way."}{" "}
                        Let&rsquo;s switch it back on before starting.
                      </InlineAlert>
                      <div style={styles.rowRight}>
                        <button
                          style={styles.secondaryBtn}
                          onClick={() =>
                            setPreflightStep(!hasActiveMic() ? "media" : "screen")
                          }
                        >
                          {!hasActiveMic() ? "Back to mic & camera" : "Back to screen share"}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {phase === "interview" && (
        <>
          {micLooksMuted ? (
            <InlineAlert type="warning">
              Your microphone looks muted — check your input device or the mute
              key on your keyboard. The interview is still running.
            </InlineAlert>
          ) : null}

          <div className="iv-grid">
            {/* Main column: welcome card first, then question + answer */}
            <div style={styles.ivMain}>
              {interviewStage === "welcome" ? (
                <Card>
                  <div style={styles.questionText}>{welcomeText}</div>
                  <div style={styles.rowRight}>
                    <button style={styles.accentBtnBig} onClick={handleWelcomeContinue}>
                      Continue
                    </button>
                  </div>
                </Card>
              ) : (
                <>
                  <Card>
                    {progress?.phase_label ? (
                      <div style={styles.ivProgressRow}>
                        <span style={styles.ivProgressChip}>
                          Part {progress.phase_index + 1} of {progress.total_phases}
                        </span>
                        <span style={styles.ivProgressLabel}>{progress.phase_label}</span>
                      </div>
                    ) : null}

                    <div style={styles.questionText}>
                      {currentQuestion || "Loading question…"}
                    </div>

                    {showScreenReminder ? (
                      <div style={styles.screenReminder} role="note">
                        <span style={styles.screenReminderIcon} aria-hidden="true">🖥</span>
                        <span>
                          <strong>Show, don’t tell</strong> — you’re already
                          sharing your screen (see the preview in the side
                          panel). Walk us through it there rather than
                          describing it.
                        </span>
                      </div>
                    ) : null}

                    {answerState === "processing" ? (
                      <div style={styles.thinkingRow}>
                        <ProcessingDots />
                        <span style={styles.thinkingText}>
                          The interviewer is thinking…
                        </span>
                      </div>
                    ) : null}
                  </Card>

                  <Card>
                    <div style={styles.answerActionsRow}>
                      <div style={styles.answerActionsLeft}>
                        <button
                          style={{
                            ...styles.accentBtn,
                            ...(answerState !== "idle" ? styles.btnDisabled : {})
                          }}
                          onClick={startAnswerRecording}
                          disabled={answerState !== "idle"}
                        >
                          Start answer
                        </button>

                        {answerState === "recording" && (
                          <div style={styles.recordingStrip}>
                            <span style={styles.recordingDot} />
                            <span style={styles.recordingStripText}>Recording your answer</span>
                          </div>
                        )}

                        <button
                          style={{
                            ...styles.accentBtn,
                            ...(answerState !== "recording" ? styles.btnDisabled : {})
                          }}
                          onClick={stopAnswerAndSend}
                          disabled={answerState !== "recording"}
                        >
                          Stop &amp; send
                        </button>
                      </div>
                    </div>

                    <p style={styles.mutedSmall}>
                      Take your time — click <b>Start answer</b>, talk it through
                      while showing your screen, then <b>Stop &amp; send</b>.
                    </p>
                  </Card>
                </>
              )}
            </div>

            {/* Side panel: self-view + session status */}
            <div style={styles.ivSide}>
              <div className="iv-selfview-wrap">
                <video
                  ref={selfViewRef}
                  className="iv-selfview"
                  muted
                  playsInline
                  autoPlay
                />
                <span className="iv-rec-chip">
                  <span className="iv-rec-dot" /> REC
                </span>
              </div>

              <div className="iv-selfview-wrap" style={{ marginTop: 10 }}>
                <video
                  ref={screenViewRef}
                  className="iv-screenview"
                  muted
                  playsInline
                  autoPlay
                />
                <span className="iv-rec-chip">Your shared screen</span>
              </div>
              <div style={styles.mutedSmall}>
                This is what the interviewer sees — show your work here.
              </div>

              <div className="pf-meter" style={{ marginTop: 10 }}>
                <div className="pf-meter-fill" ref={meterFillRef} />
              </div>

              <div style={styles.ivSideStatus}>
                {micLooksMuted ? (
                  <span style={styles.ivSideStatusWarn}>Mic looks muted</span>
                ) : (
                  <span style={styles.ivSideStatusOk}>
                    Mic &amp; screen sharing on
                  </span>
                )}
              </div>

              <div style={styles.ivSideDivider} />

              <button
                style={styles.ivSideBtn}
                onClick={async () => {
                  await ensureAudioContextUnlocked();
                  setToast("");
                  setAiVoicePanelOpen((v) => !v);
                }}
              >
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  AI Voice <EditIcon style={{ fontSize: 15 }} />
                </span>
              </button>

              <button
                style={styles.ivFinishBtn}
                onClick={() => setConfirmEndOpen(true)}
                disabled={answerState === "processing"}
              >
                Finish early…
              </button>
            </div>
          </div>

          {confirmEndOpen && (
            <Modal onClose={() => setConfirmEndOpen(false)}>
              <h3 style={styles.h3}>When you end an interview:</h3>
              <ul style={styles.ul}>
                <li>We’ll upload what you’ve recorded so far.</li>
                <li>You can’t resume once it ends.</li>
                <li><strong>We’ll do a quick quality review</strong> (detailed answers + clear product usage) so we can pay you. Payment is usually sent within <strong>2–3 business days.</strong></li>
              </ul>
              <div style={styles.rowRight}>
                <button style={styles.secondaryBtn} onClick={() => setConfirmEndOpen(false)}>
                  Cancel
                </button>
                <button style={styles.dangerBtn} onClick={handleManualEndInterview}>
                  End &amp; upload
                </button>
              </div>
            </Modal>
          )}
        </>
      )}

      {phase === "upload" && (
        <Card>
          {wrapStage === "complete" ? (
            <>
              {/* Congratulations moment */}
              <div style={styles.doneHero}>
                <div style={styles.doneCheck} aria-hidden="true">✓</div>
                <h2 style={styles.doneH1}>Congratulations — you’re done!</h2>
                <p style={styles.doneSub}>
                  Your interview uploaded successfully. Thanks for walking us
                  through your real work — that’s exactly what makes this
                  research useful.
                </p>
              </div>

              <div style={styles.nextCard}>
                <div style={styles.nextTitle}>What happens next</div>
                <div style={{ display: "grid", gap: 10 }}>
                  <PfTip n="1">
                    A person reviews your recording — we check for real,
                    detailed examples.
                  </PfTip>
                  <PfTip n="2">
                    Payment is sent within <strong>2–3 business days</strong> of
                    approval
                    {meta?.payment_method ? (
                      <>
                        {" "}via <strong>{humanizePaymentMethod(meta.payment_method)}</strong>
                        {meta?.payment_email ? <> ({meta.payment_email})</> : null}
                      </>
                    ) : null}
                    .
                  </PfTip>
                  <PfTip n="3">
                    You have <strong>48 hours</strong> after processing to
                    request edits or full removal, no questions asked.
                  </PfTip>
                </div>
              </div>

              {/* Light-touch feedback */}
              <div style={styles.feedbackBox}>
                {!exitSubmitted ? (
                  <>
                    <div style={styles.feedbackTitle}>How was the experience?</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                      <Rating
                        name="exit-rating"
                        max={10}
                        value={exitRating}
                        onChange={(e, v) => setExitRating(v)}
                      />
                      <Typography variant="body2">
                        {exitRating ? `${exitRating}/10` : "1–10"}
                      </Typography>
                    </div>

                    <TextField
                      label="Anything we should improve? (optional)"
                      placeholder="A sentence is plenty."
                      value={exitWhy}
                      onChange={(e) => setExitWhy(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                    />

                    {exitError ? (
                      <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                        {exitError}
                      </Typography>
                    ) : null}

                    <div style={{ marginTop: 12 }}>
                      <Button
                        variant="contained"
                        onClick={submitExitFeedback}
                        disabled={exitSubmitting || !exitRating}
                      >
                        {exitSubmitting ? "Submitting…" : "Send feedback"}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Typography variant="body1">Thanks — feedback received.</Typography>
                )}
              </div>

              <p style={styles.mutedSmall}>You can close this tab whenever you like.</p>
            </>
          ) : (
            <>
              <h2 style={styles.h2}>
                {wrapStage === "finalizing"
                  ? "Almost done — finalizing…"
                  : wrapStage === "failed"
                  ? "Upload problem"
                  : "Almost done — uploading your recording"}
              </h2>

              {wrapStage === "failed" ? (
                <InlineAlert type="error">
                  Upload failed. Please keep this tab open and retry.
                </InlineAlert>
              ) : (
                <InlineAlert type={endReasonLocal && endReasonLocal !== "user_ended" ? "warning" : "info"}>
                  {endReasonLocal && endReasonLocal !== "user_ended" ? (
                    <>
                      {humanizeEndReason(endReasonLocal)}{" "}
                      If this was a mistake, you can restart and keep mic +
                      screen share on for the full session.
                    </>
                  ) : (
                    <>Keep this tab open until the upload finishes — it won’t take long.</>
                  )}
                </InlineAlert>
              )}

              {/* The interviewer's parting words. Without this the view simply
                  swapped mid-farewell, which read as the interview breaking
                  rather than finishing. */}
              {farewellText ? (
                <div style={styles.farewellText}>“{farewellText}”</div>
              ) : null}

              {/* Upload items */}
              <div style={styles.uploadGrid}>
                <UploadRow
                  label="Webcam video"
                  status={effectiveWebcam}
                  progress={uploadProgress.webcam}
                />
                <UploadRow
                  label="Screen recording"
                  status={effectiveScreen}
                  progress={uploadProgress.screen}
                />
              </div>

              {(wrapStage === "uploading" || wrapStage === "finalizing") ? (
                <div style={styles.processingBox}>
                  <div style={styles.processingText}>
                    {wrapStage === "uploading"
                      ? "Step 1 of 2: Uploading files…"
                      : "Step 2 of 2: Finalizing… Almost done."}
                  </div>
                  <ProcessingDots />
                </div>
              ) : null}

              <div style={styles.rowWrap}>
                {wrapStage === "failed" && (
                  <button style={styles.accentBtn} onClick={retryUpload}>
                    Retry upload
                  </button>
                )}

                {/* Only show "Restart interview" when something went wrong or ended early */}
                {(wrapStage === "failed" || (endReasonLocal && endReasonLocal !== "user_ended")) ? (
                  <button
                    style={styles.secondaryBtn}
                    onClick={restartInterview}
                    disabled={wrapStage === "uploading" || wrapStage === "finalizing"}
                  >
                    Restart interview
                  </button>
                ) : null}
              </div>

              {wrapStage === "failed" ? (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: "pointer" }}>Troubleshooting</summary>
                  <ul style={styles.ul}>
                    <li>Keep this tab open until upload completes.</li>
                    <li>If upload fails, click “Retry upload”.</li>
                    <li>If your mic or screen sharing stopped early, you may need to restart.</li>
                  </ul>
                </details>
              ) : null}
            </>
          )}
        </Card>
      )}

      {aiVoicePanelOpen && (
        <Modal onClose={() => setAiVoicePanelOpen(false)}>
          <h3 style={styles.h3}>AI Voice</h3>

          <label style={styles.labelBlock}>
            <input
              type="checkbox"
              checked={ttsEnabled}
              onChange={(e) => setTtsEnabled(e.target.checked)}
            />{" "}
            Speak questions out loud
          </label>

          <div style={styles.rowWrap}>
            <div style={styles.field}>
              <div style={styles.fieldLabel}>Style</div>
              <select
                value={voicePersona}
                onChange={(e) => setVoicePersona(e.target.value)}
                style={styles.select}
                disabled={!ttsEnabled}
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
              </select>
            </div>

            <div style={styles.field}>
              <div style={styles.fieldLabel}>Voice preference</div>
              <select
                value={voiceGender}
                onChange={(e) => setVoiceGender(e.target.value)}
                style={styles.select}
                disabled={!ttsEnabled}
              >
                <option value="female">Prefer higher pitch</option>
                <option value="male">Prefer lower pitch</option>
              </select>
            </div>

            <div style={styles.field}>
              <div style={styles.fieldLabel}>Voice</div>
              <select
                value={selectedVoiceName || ""}
                onChange={(e) => setSelectedVoiceName(e.target.value)}
                style={styles.select}
                disabled={!ttsEnabled}
              >
                <option value="">Auto (recommended)</option>
                {OPENAI_TTS_VOICES.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.rowRight}>
            <button style={styles.secondaryBtn} onClick={() => setAiVoicePanelOpen(false)}>
              Done
            </button>
          </div>
        </Modal>
      )}
    </ZoomLitePage>
  );
}

/* =========================
   UI building blocks
========================= */

function ZoomLitePage({ children }) {
  return (
    <div style={styles.page}>
      <div style={styles.container}>{children}</div>
    </div>
  );
}

function HeaderRow({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.h1}>{title}</div>
      {subtitle ? <div style={styles.subtitle}>{subtitle}</div> : null}
    </div>
  );
}

function Stepper({ current }) {
  const idx = PHASES.indexOf(current);
  const labels = [
    { key: "preflight", label: "Get set up" },
    { key: "interview", label: "Interview" },
    { key: "upload", label: "Finish" },
  ];

  return (
    <div style={styles.stepper}>
      {labels.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.key} style={styles.stepItem}>
            <div style={{ ...styles.stepDot, ...(active ? styles.stepDotActive : done ? styles.stepDotDone : {}) }}>
              {i + 1}
            </div>
            <div style={{ ...styles.stepLabel, ...(active ? styles.stepLabelActive : {}) }}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function Badge({ label, tone }) {
  const base = { ...styles.badge };
  const toneStyle =
    tone === "good" ? styles.badgeGood :
    tone === "warn" ? styles.badgeWarn :
    tone === "bad" ? styles.badgeBad :
    styles.badgeNeutral;

  return <div style={{ ...base, ...toneStyle }}>{label}</div>;
}

function Divider() {
  return <div style={styles.divider} />;
}

/* ===== Pre-flight wizard bits ===== */

function PfRailItem({ n, label, state }) {
  // state: "done" | "active" | "todo"
  const iconStyle =
    state === "done"
      ? styles.pfRailIconDone
      : state === "active"
      ? styles.pfRailIconActive
      : styles.pfRailIconTodo;

  return (
    <div
      style={{
        ...styles.pfRailItem,
        ...(state === "active" ? styles.pfRailItemActive : {}),
      }}
    >
      <span style={{ ...styles.pfRailIcon, ...iconStyle }}>
        {state === "done" ? "✓" : n}
      </span>
      <span
        style={{
          ...styles.pfRailLabel,
          ...(state === "todo" ? styles.pfRailLabelTodo : {}),
        }}
      >
        {label}
      </span>
    </div>
  );
}

function CheckRow({ result }) {
  const { status, label, detail } = result;
  return (
    <div style={styles.pfCheckRow}>
      <span style={styles.pfCheckIconWrap}>
        {status === "pending" ? (
          <span className="pf-spin" />
        ) : status === "pass" ? (
          <span style={styles.pfCheckPass}>✓</span>
        ) : (
          <span style={styles.pfCheckFail}>✕</span>
        )}
      </span>
      <span>
        <span style={styles.pfCheckLabel}>{label}</span>
        {detail ? <span style={styles.pfCheckDetail}> — {detail}</span> : null}
      </span>
    </div>
  );
}

function PfTip({ n, children }) {
  return (
    <div style={styles.pfTipRow}>
      <span style={styles.pfTipBadge}>{n}</span>
      <span style={styles.pfTipText}>{children}</span>
    </div>
  );
}

function UploadRow({ label, status, progress }) {
  const norm = status || "missing";

  const tone =
    norm === "complete" ? "good" :
    norm === "failed" ? "bad" :
    norm === "uploading" ? "warn" :
    "neutral";

  const uploading = norm === "uploading";
  // A percentage only while it means something: "Uploading… 0%" for a whole
  // minute is worse than no number at all, so an uncomputable total shows the
  // indeterminate bar instead.
  const hasPct = uploading && typeof progress === "number";

  const display =
    norm === "complete" ? "Uploaded" :
    uploading ? (hasPct ? `Uploading… ${progress}%` : "Uploading…") :
    norm === "failed" ? "Failed" :
    "Not received";

  return (
    <div>
      <div style={styles.uploadRow}>
        <div style={styles.uploadLabel}>{label}</div>
        <Badge label={display} tone={tone} />
      </div>
      {uploading ? (
        <div className="up-bar">
          <div
            className={hasPct ? "up-bar-fill" : "up-bar-fill up-bar-indeterminate"}
            style={hasPct ? { width: `${progress}%` } : undefined}
          />
        </div>
      ) : null}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div style={styles.modalCard}>
        {children}
        <button style={styles.modalClose} onClick={onClose} aria-label="Close modal">×</button>
      </div>
    </div>
  );
}

function ProcessingDots() {
  return (
    <div style={styles.dots} aria-label="Processing">
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
    </div>
  );
}

const processingCss = `
.dots { display:flex; gap:6px; align-items:center; margin-top:8px; }
.dots .dot { width:8px; height:8px; border-radius:999px; background:#111827; opacity:.2; animation: pulse 1s infinite; }
.dots .dot:nth-child(2) { animation-delay: .15s; }
.dots .dot:nth-child(3) { animation-delay: .3s; }
@keyframes pulse { 0%{opacity:.2} 50%{opacity:.9} 100%{opacity:.2} }

/* Upload progress. Determinate where the browser can compute a total, an
   indeterminate sweep where it can't — either way the bar moves, which is the
   point: the row alone read as a stalled screen. */
.up-bar {
  height:6px; border-radius:999px; background:#E5E7EB;
  overflow:hidden; margin:6px 0 10px;
}
.up-bar-fill {
  height:100%; background:#2563EB; border-radius:999px;
  transition: width .25s ease;
}
.up-bar-indeterminate {
  width:35%;
  animation: upsweep 1.2s ease-in-out infinite;
}
@keyframes upsweep {
  0%   { margin-left:-35%; }
  100% { margin-left:100%; }
}
`;

/* Pre-flight wizard layout + widgets that need media queries / animation. */
const preflightCss = `
.pf-grid { display:grid; grid-template-columns: 180px minmax(0,1fr); gap:24px; align-items:start; }
@media (max-width: 700px) {
  .pf-grid { grid-template-columns: 1fr; gap:14px; }
  .pf-rail-mobile-row { flex-direction: row !important; flex-wrap: wrap; }
}
.pf-selfview {
  display:block; width:100%; max-width:340px; aspect-ratio:16/10;
  border-radius:12px; background:#111827; object-fit:cover;
  transform:scaleX(-1); /* mirror — people expect to see themselves as in a mirror */
}
.pf-meter { height:10px; border-radius:999px; background:#E5E7EB; overflow:hidden; }
.pf-meter-fill { height:100%; width:0%; border-radius:999px; background:linear-gradient(90deg,#34D399,#10B981); transition:width 90ms linear; }
.pf-spin {
  display:inline-block; width:13px; height:13px; border-radius:999px;
  border:2px solid #BFDBFE; border-top-color:#2563EB;
  animation:pfspin .8s linear infinite;
}
@keyframes pfspin { to { transform: rotate(360deg); } }

/* Live interview: main column + self-view side panel */
.iv-grid { display:grid; grid-template-columns: minmax(0,1fr) 260px; gap:12px; align-items:start; }
@media (max-width: 760px) { .iv-grid { grid-template-columns: 1fr; } }
.iv-selfview-wrap { position:relative; }
.iv-selfview {
  display:block; width:100%; aspect-ratio:16/10;
  border-radius:10px; background:#111827; object-fit:cover;
  transform:scaleX(-1);
}
/* Shared-screen preview: unmirrored, letterboxed so the content stays readable */
.iv-screenview {
  display:block; width:100%; aspect-ratio:16/10;
  border-radius:10px; background:#111827; object-fit:contain;
}
.iv-rec-chip {
  position:absolute; top:8px; left:8px;
  display:inline-flex; align-items:center; gap:6px;
  background:rgba(17,24,39,.72); color:#fff;
  font-size:11px; font-weight:600; letter-spacing:.04em;
  padding:3px 8px; border-radius:999px;
}
.iv-rec-dot { width:7px; height:7px; border-radius:999px; background:#EF4444; animation:pulse 1.2s infinite; }
`;

const styles = {
  ...ui,

  field: { minWidth: 180 },

  container: {
    maxWidth: 840,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  h1: { fontSize: 24, fontWeight: 700, color: "#111827" },
  subtitle: { fontSize: 14, color: "#6B7280", marginTop: 4 },

  stepper: {
    display: "flex",
    gap: 16,
    padding: "10px 12px",
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    alignItems: "center",
  },
  stepItem: { display: "flex", alignItems: "center", gap: 8 },
  stepDot: {
    width: 24, height: 24, borderRadius: 999,
    background: "#E5E7EB", color: "#111827",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 700,
  },
  stepDotActive: { background: colors.accent, color: "#fff" },
  stepDotDone: { background: "#10B981", color: "#fff" },
  stepLabel: { fontSize: 13, color: "#374151", fontWeight: 600 },
  stepLabelActive: { color: "#111827" },

  badge: {
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #E5E7EB",
  },
  badgeGood: { background: "#ECFDF5", color: "#065F46", borderColor: "#A7F3D0" },
  badgeWarn: { background: "#FFFBEB", color: "#92400E", borderColor: "#FDE68A" },
  badgeBad: { background: "#FEF2F2", color: "#991B1B", borderColor: "#FECACA" },
  badgeNeutral: { background: "#F9FAFB", color: "#374151", borderColor: "#E5E7EB" },


  recordingStrip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
    marginBottom: 10,
  },
  recordingDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "#B91C1C",
    boxShadow: "0 0 0 3px rgba(185, 28, 28, 0.15)", // subtle ring
  },
  recordingStripText: {
    fontSize: 13,
    fontWeight: 800,
    color: "#111827",
  },

  h2: { fontSize: 18, fontWeight: 800, margin: "0 0 10px", color: "#111827" },
  h3: { fontSize: 16, fontWeight: 800, margin: "0 0 10px", color: "#111827" },

  muted: { fontSize: 13, color: "#6B7280", marginTop: 0, marginBottom: 10 },
  mutedSmall: { fontSize: 12, color: "#6B7280", marginTop: 10, marginBottom: 0 },

  nextCard: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
  },
  nextTitle: { fontSize: 13, fontWeight: 900, color: "#111827", marginBottom: 6 },

  row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  rowWrap: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  rowRight: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 },
  rowBetween: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },

  dangerBtn: {
    background: "#991B1B",
    color: "#fff",
    border: "1px solid #991B1B",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 900,
    cursor: "pointer",
  },





  labelInline: { fontSize: 13, fontWeight: 700, color: "#111827" },
  labelBlock: { display: "block", marginTop: 12, fontSize: 13, fontWeight: 700, color: "#111827" },

  questionText: { fontSize: 16, fontWeight: 700, color: "#111827", lineHeight: 1.4 },

  processingBox: {
    padding: 12,
    borderRadius: 12,
    border: "1px dashed #D1D5DB",
    background: "#F9FAFB",
  },
  processingText: { fontSize: 13, fontWeight: 800, color: "#374151" },


  uploadGrid: { display: "grid", gap: 10, marginTop: 10 },
  uploadRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
  uploadLabel: { fontSize: 13, fontWeight: 800, color: "#111827" },

  modalBackdrop: {
    position: "fixed", inset: 0, background: "rgba(17,24,39,.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 12, zIndex: 50,
  },
  modalCard: {
    width: "min(520px, 100%)",
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    padding: 16,
    position: "relative",
  },
  modalClose: {
    position: "absolute",
    top: 8,
    right: 10,
    border: "none",
    background: "transparent",
    fontSize: 22,
    cursor: "pointer",
    color: "#6B7280",
  },
  dots: { display: "flex" },

  answerActionsRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },

  answerActionsLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  answerActionsRight: {
    marginLeft: "auto", // pushes it to the far right
    display: "flex",
    justifyContent: "flex-end",
  },

  /* ===== Pre-flight wizard ===== */

  accentBtn: { ...ui.primaryBtn },
  accentBtnBig: { ...ui.primaryBtnBig },

  pfRail: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  pfRailItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 10,
  },
  pfRailItemActive: {
    background: "#EFF6FF",
  },
  pfRailIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flex: "0 0 auto",
  },
  pfRailIconDone: { background: "#10B981", color: "#fff" },
  pfRailIconActive: { background: "#2563EB", color: "#fff" },
  pfRailIconTodo: { background: "#E5E7EB", color: "#6B7280" },
  pfRailLabel: { fontSize: 13, fontWeight: 600, color: "#111827" },
  pfRailLabelTodo: { color: "#9CA3AF", fontWeight: 500 },

  pfCheckRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    fontSize: 13,
    lineHeight: 1.45,
  },
  pfCheckIconWrap: {
    width: 18,
    display: "inline-flex",
    justifyContent: "center",
    marginTop: 1,
    flex: "0 0 auto",
  },
  pfCheckPass: { color: "#059669", fontWeight: 700 },
  pfCheckFail: { color: "#B91C1C", fontWeight: 700 },
  pfCheckLabel: { fontWeight: 600, color: "#111827" },
  pfCheckDetail: { color: "#6B7280", fontWeight: 400 },

  pfHeardOk: {
    marginTop: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "#059669",
  },
  pfHeardHint: {
    marginTop: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
  },

  pfPickerRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 4,
  },
  pfPickerGood: {
    border: "1px solid #A7F3D0",
    background: "#ECFDF5",
    borderRadius: 10,
    padding: "8px 12px",
    minWidth: 120,
  },
  pfPickerBad: {
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    borderRadius: 10,
    padding: "8px 12px",
    minWidth: 120,
    opacity: 0.85,
  },
  pfPickerTitle: { fontSize: 13, fontWeight: 600, color: "#111827" },
  pfPickerNote: { fontSize: 11, fontWeight: 500, color: "#6B7280", marginTop: 2 },

  /* ===== Finish (congratulations) ===== */

  doneHero: {
    textAlign: "center",
    padding: "18px 12px 6px",
  },
  doneCheck: {
    width: 52,
    height: 52,
    borderRadius: 999,
    margin: "0 auto 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ECFDF5",
    border: "2px solid #34D399",
    color: "#059669",
    fontSize: 26,
    fontWeight: 700,
  },
  doneH1: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 8px",
  },
  doneSub: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 1.55,
    maxWidth: 460,
    margin: "0 auto 18px",
  },

  feedbackBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#fff",
  },
  feedbackTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 10,
  },

  pfTipRow: { display: "flex", alignItems: "flex-start", gap: 10 },
  pfTipBadge: {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EFF6FF",
    color: "#1D4ED8",
    fontSize: 12,
    fontWeight: 700,
    flex: "0 0 auto",
    marginTop: 1,
  },
  pfTipText: { fontSize: 13, color: "#111827", lineHeight: 1.5 },

  /* ===== Live interview (two-panel) ===== */

  ivMain: { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },

  ivProgressRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  ivProgressChip: {
    fontSize: 12,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
    background: "#EFF6FF",
    color: "#1D4ED8",
  },
  ivProgressLabel: { fontSize: 12, fontWeight: 500, color: "#6B7280" },

  // Highlighted so it reads as an instruction attached to the question, not as
  // an error. Amber rather than the accent blue, which is used for navigation.
  screenReminder: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 14,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #FDE68A",
    background: "#FFFBEB",
    color: "#92400E",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.45,
  },
  screenReminderIcon: { fontSize: 15, lineHeight: 1.3, flex: "0 0 auto" },

  thinkingRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 12,
  },
  thinkingText: { fontSize: 13, fontWeight: 500, color: "#6B7280" },

  farewellText: {
    marginTop: 12,
    fontSize: 15,
    lineHeight: 1.5,
    color: "#374151",
    fontStyle: "italic",
  },


  ivSide: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 16,
    padding: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,.04)",
    display: "flex",
    flexDirection: "column",
  },
  ivSideStatus: { marginTop: 8 },
  ivSideStatusOk: { fontSize: 12, fontWeight: 500, color: "#059669" },
  ivSideStatusWarn: { fontSize: 12, fontWeight: 600, color: "#B45309" },
  ivSideDivider: { height: 1, background: "#E5E7EB", margin: "12px 0" },
  ivSideBtn: {
    background: "#F9FAFB",
    color: "#111827",
    border: "1px solid #E5E7EB",
    padding: "8px 10px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
  ivFinishBtn: {
    marginTop: 10,
    background: "transparent",
    color: "#6B7280",
    border: "none",
    padding: "6px 0",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 3,
    alignSelf: "center",
  },

};
